import { QueryObserverResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { useHostingAdvancedServer } from '@newfold/huapi-js';

import {
  REIMAGE_FAILED_STATUSES,
  REIMAGE_IN_PROGRESS_STATUSES,
} from '../utils/constants';

type RefetchServerStatus = () => Promise<
  QueryObserverResult<string | undefined, AxiosError>
>;

interface UseReimageStatusReturn {
  serverStatus?: string;
  isReimaging: boolean;
  reimageFailed: boolean;
  refetchServerStatus: RefetchServerStatus;
  setIsReimaging: (value: boolean) => void;
  setReimageFailed: (value: boolean) => void;
}

/**
 * Hook to manage reimage status based on server status
 * Checks server status on mount and updates reimage state accordingly
 */
export const useReimageStatus = (
  hostingId: number | string,
): UseReimageStatusReturn => {
  const [isReimaging, setIsReimaging] = useState(false);
  const [reimageFailed, setReimageFailed] = useState(false);

  const { data: status, refetch: refetchServerStatus } =
    useHostingAdvancedServer(hostingId as any, {
      query: {
        enabled: !!hostingId,
        select: (data) => data.data.status,
      },
    });

  // Check server status and set reimaging/failed state
  useEffect(() => {
    if (!status) return;

    if (REIMAGE_IN_PROGRESS_STATUSES.includes(status)) {
      setIsReimaging(true);
      setReimageFailed(false);
    } else if (REIMAGE_FAILED_STATUSES.includes(status)) {
      setIsReimaging(false);
      setReimageFailed(true);
    } else if (status === 'active') {
      setIsReimaging(false);
      setReimageFailed(false);
    }
  }, [status]);

  return {
    serverStatus: status,
    isReimaging,
    reimageFailed,
    refetchServerStatus,
    setIsReimaging,
    setReimageFailed,
  };
};
